<form
  novalidate
  class="flex flex-col"
  [formGroup]="patientForm"
  (ngSubmit)="submit()"
>
  <mat-error
    *ngIf="patientForm.contactDetailsError$ | async as contactDetailsError"
  >
    {{ contactDetailsError }}
  </mat-error>

  @if (patient$ | async) {
    <div class="mb-6 mt-2 flex-1">
      <mat-form-field class="w-full">
        <mat-label>Name</mat-label>
        <input matInput formControlName="name" required />
        <mat-hint>
          {{ nameMatHint }}
        </mat-hint>
      </mat-form-field>
    </div>
  } @else {
    <ng-container
      *ngrxLet="suggestedPatientData$ | async as suggestedPatientData"
    >
      <div class="mb-6 mt-2">
        <pr-patient-selector
          placeholder="Name"
          [suggestedPatientData]="suggestedPatientData"
          [formControl]="patientSelectorCtrl"
          [disableAllPatients]="true"
          [matHint]="nameMatHint"
          (clear)="clearPatient()"
        />
      </div>
    </ng-container>
  }

  <pt-iso-date-selector
    class="mb-4"
    placeholder="Date of Birth"
    formControlName="dateOfBirth"
    [required]="'dateOfBirth' | map: isRequired$ : this | async"
    [max]="today"
  />

  <mat-form-field>
    <mat-label>Email</mat-label>
    <input
      matInput
      type="email"
      formControlName="email"
      [required]="'email' | map: isRequired$ : this | async"
      [errorStateMatcher]="errorStateMatcher"
    />
    @if (patientForm.controls.email.hasError('email')) {
      <mat-error>Please enter a valid email address</mat-error>
    }
  </mat-form-field>

  <div formArrayName="contactNumbers" class="flex flex-col">
    <ng-container
      *ngFor="
        let control of contactNumbers.controls;
        trackBy: trackByIndex;
        index as index;
        first as isFirst
      "
    >
      <div>
        <pr-contact-number-input
          [form]="control"
          [isFirst]="isFirst"
          [index]="index"
          [required]="'contactNumbers' | map: isRequired$ : this | async"
        >
          <div class="flex flex-row">
            <button
              mat-icon-button
              (click)="contactNumberControls.addControl(index)"
            >
              <mat-icon>add</mat-icon>
            </button>
            <button
              mat-icon-button
              (click)="contactNumberControls.removeControl(index)"
            >
              <mat-icon>remove</mat-icon>
            </button>
          </div>
        </pr-contact-number-input>
      </div>
    </ng-container>
  </div>

  <pr-address-input class="mb-4" formControlName="address" />

  <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
    <mat-form-field class="flex-1">
      <mat-label>Gender</mat-label>
      <mat-select formControlName="gender" required>
        <mat-option
          *ngFor="let gender of genders; trackBy: trackByGender"
          [value]="gender"
        >
          {{ gender | splitCamel | titlecase }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="flex-1">
      <mat-label>Status</mat-label>
      <mat-select formControlName="status" required>
        <mat-option
          *ngFor="let status of statuses; trackBy: trackByStatus"
          [value]="status"
        >
          {{ status | splitCamel | titlecase }}
        </mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <pr-referrer-selector
    class="w-full flex-1"
    formControlName="referrer"
    [required]="'referrer' | map: isRequired$ : this | async"
  />

  <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
    <mat-form-field class="flex-1">
      <mat-label>Preferred Dentist</mat-label>
      <mat-select
        formControlName="preferredDentist"
        [compareWith]="isSelectedNamedDocument"
      >
        <mat-option
          *ngFor="
            let practitioner of practitioners$ | async;
            trackBy: trackByPractitioner
          "
          [value]="practitioner"
        >
          {{ practitioner.name }}
        </mat-option>
        <mat-option [value]="undefined">None</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="flex-1">
      <mat-label>Preferred Hygienist</mat-label>
      <mat-select
        formControlName="preferredHygienist"
        [compareWith]="isSelectedNamedDocument"
      >
        <mat-option
          *ngFor="
            let practitioner of practitioners$ | async;
            trackBy: trackByPractitioner
          "
          [value]="practitioner"
        >
          {{ practitioner.name }}
        </mat-option>
        <mat-option [value]="undefined">None</mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
    <mat-form-field class="flex-1">
      <mat-label>Preferred Practice</mat-label>
      <mat-select
        formControlName="preferredPractice"
        [compareWith]="isSelectedNamedDocument"
      >
        <mat-option
          *ngFor="let practice of practices$ | async; trackBy: trackByPractice"
          [value]="practice"
        >
          {{ practice.name }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="flex-1" floatLabel="always">
      <mat-label>Preferred Fee Schedule</mat-label>
      <mat-select
        formControlName="preferredFeeSchedule"
        [compareWith]="isSelectedNamedDocument"
        [placeholder]="feeScheduleDefault$ | async"
      >
        <mat-option [value]="undefined">
          {{ feeScheduleDefault$ | async }}
        </mat-option>

        @if (staleFeeSchedule$ | async; as staleFeeSchedule) {
          <mat-option [value]="staleFeeSchedule" class="text-warn-500">
            {{ staleFeeSchedule.name }} (Unavailable)
          </mat-option>
        }

        @for (group of feeScheduleGroups$ | async; track group.name) {
          <div class="p-2 text-xs font-bold opacity-75">
            {{ group.name | titlecase }}
            <mat-icon
              class="icon-3 opacity-50"
              matTooltip="Fee Schedules from {{ group.scope | titlecase }}"
              >info</mat-icon
            >
          </div>

          @for (feeSchedule of group.schedules; track feeSchedule.ref.path) {
            <mat-option [value]="feeSchedule">
              {{ feeSchedule.name }}
            </mat-option>
          }
        }
      </mat-select>
      @if (staleFeeSchedule$ | async) {
        <mat-hint class="text-warn-500">
          This fee schedule is no longer available. Please select a different
          one.
        </mat-hint>
      }
    </mat-form-field>
  </div>

  <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
    <ng-container *prIsRegion="[region.NewZealand]" formGroupName="nz">
      <ng-container formGroupName="moh">
        <mat-form-field class="col-span-1 md:col-span-2">
          <mat-label>School Attended</mat-label>
          <input
            placeholder="Search schools..."
            formControlName="school"
            matInput
            [matAutocomplete]="schoolAuto"
          />
          <mat-autocomplete
            #schoolAuto="matAutocomplete"
            [displayWith]="schoolDisplayFn"
          >
            @for (
              school of schoolSearch.results$ | async;
              track school.schoolNumber
            ) {
              <mat-option [value]="school">
                {{ school.schoolName }}
              </mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </ng-container>
    </ng-container>

    <pr-tags-input
      class="col-span-1 md:col-span-2"
      formControlName="tags"
      [tagCol]="tagCol$ | async"
      [tagType]="tagType"
    />

    @for (
      metadata of patientMetadataControls$ | async;
      track metadata.configuration.key
    ) {
      <pr-edit-patient-metadata
        *ptFeatureEnabled="metadata.configuration.feature"
        class="flex-1"
        [configuration]="metadata.configuration"
        [formControl]="metadata.control"
      />
    }
  </div>

  @if (depositsEnabled$ | async) {
    <div class="mt-2">
      <mat-slide-toggle
        formControlName="depositRequired"
        color="primary"
        aria-describedby="edit-patient-deposit-hint"
      >
        Deposit required
      </mat-slide-toggle>
      <p
        id="edit-patient-deposit-hint"
        class="mat-caption mb-0 mt-1 text-sm opacity-80"
      >
        When enabled, this patient requires a deposit even when the treatment is
        excluded by template, booking context, or appointment day settings. The
        brand deposit master switch must still be enabled.
      </p>
    </div>
  }
</form>
